<template>
  <Dialog
    :visible="visible"
    @update:visible="$emit('update:visible', $event)"
    modal
    header="Manual Campaign Setup Details"
    :style="{ width: '650px' }"
    :breakpoints="{ '768px': '90vw' }"
  >
    <div v-if="ad">
      <Message
        severity="warn"
        icon="pi pi-exclamation-triangle"
        :closable="false"
        class="mb-3"
        :pt="{
          icon: {
            class: 'mt-1 text-xl align-self-start',
          },
          text: {
            class: 'font-normal text-sm',
          },
        }"
      >
        As a manual provider, you need to configure this campaign in your own
        ad serving system. Copy the details below and paste them into your system.
      </Message>

      <div class="detail-section">
        <h3 class="mt-0 mb-3">Campaign Information</h3>

        <div class="detail-row">
          <span class="detail-label">Campaign Name</span>
          <div class="detail-value">{{ ad.campaign_name || '-' }}</div>
        </div>

        <div class="detail-row">
          <span class="detail-label">Start Date</span>
          <div class="detail-value">{{ formatDate(ad.start_date) }}</div>
        </div>

        <div class="detail-row">
          <span class="detail-label">End Date</span>
          <div class="detail-value">{{ formatDate(ad.end_date) }}</div>
        </div>

        <div class="detail-row">
          <span class="detail-label">CPM Rate</span>
          <div class="detail-value">${{ Number(ad.bid_cap || 0).toFixed(2) }}</div>
        </div>

        <div class="detail-row">
          <span class="detail-label">Impression Goal</span>
          <div class="detail-value">{{ Number(ad.imp_goal || 0).toLocaleString() }}</div>
        </div>

        <div class="detail-row">
          <span class="detail-label">Frequency Cap</span>
          <div class="detail-value">{{ ad.frequency_cap || 'None' }}</div>
        </div>

        <div class="detail-row">
          <span class="detail-label">Ad Run Slot</span>
          <div class="detail-value">{{ ad.ad_run_slot || '-' }}</div>
        </div>

        <div class="detail-row">
          <span class="detail-label">Ad Length</span>
          <div class="detail-value">{{ ad.ad_length ? `${ad.ad_length}s` : '-' }}</div>
        </div>
      </div>

      <Divider />

      <div class="detail-section">
        <h3 class="mt-0 mb-3">VAST Tag</h3>
        <p class="text-sm text-gray-500 mt-0 mb-2">
          Copy this VAST tag URL and paste it into your ad serving system.
        </p>
        <div class="vast-tag-container">
          <InputText
            class="w-full font-mono text-sm"
            :modelValue="vastTagUrl"
            readonly
          />
          <Button
            :icon="copySuccess === 'vast' ? 'pi pi-check' : 'pi pi-copy'"
            :severity="copySuccess === 'vast' ? 'success' : 'secondary'"
            outlined
            class="ml-2 flex-shrink-0"
            @click="copyToClipboard(vastTagUrl, 'vast')"
            v-tooltip.top="'Copy VAST Tag URL'"
          />
        </div>
      </div>

      <Divider />

      <div class="detail-section">
        <h3 class="mt-0 mb-3">Tracking Pixels</h3>
        <p class="text-sm text-gray-500 mt-0 mb-2">
          Configure these tracking URLs to fire when the ad starts.
        </p>
        <div class="text-sm font-semibold mb-1">Ekoz Start Pixel</div>
        <div class="vast-tag-container mb-3">
          <InputText
            class="w-full font-mono text-sm"
            :modelValue="ekozStartPixelUrl"
            readonly
          />
          <Button
            :icon="copySuccess === 'ekoz-start' ? 'pi pi-check' : 'pi pi-copy'"
            :severity="copySuccess === 'ekoz-start' ? 'success' : 'secondary'"
            outlined
            class="ml-2 flex-shrink-0"
            @click="copyToClipboard(ekozStartPixelUrl, 'ekoz-start')"
            v-tooltip.top="'Copy Ekoz Start Pixel URL'"
          />
        </div>
        <div class="text-sm font-semibold mb-1">
          Advertiser Tracking Pixels
        </div>
        <template v-if="trackingPixels.length">
          <div
            v-for="(pixel, index) in trackingPixels"
            :key="`${index}-${pixel}`"
            class="vast-tag-container mb-2"
          >
            <InputText
              class="w-full font-mono text-sm"
              :modelValue="pixel"
              readonly
            />
            <Button
              :icon="copySuccess === `pixel-${index}` ? 'pi pi-check' : 'pi pi-copy'"
              :severity="copySuccess === `pixel-${index}` ? 'success' : 'secondary'"
              outlined
              class="ml-2 flex-shrink-0"
              @click="copyToClipboard(pixel, `pixel-${index}`)"
              v-tooltip.top="'Copy Tracking Pixel URL'"
            />
          </div>
        </template>
        <p v-else class="text-sm text-gray-500 mb-0">
          No tracking pixels were provided for this campaign.
        </p>
      </div>

      <Divider />

      <div class="detail-section">
        <h3 class="mt-0 mb-3">Audio Ad URL</h3>
        <p class="text-sm text-gray-500 mt-0 mb-2">
          Direct link to the audio ad file (if you need it outside the VAST tag).
        </p>
        <div class="vast-tag-container">
          <InputText
            class="w-full font-mono text-sm"
            :modelValue="ad.s3_signed_url || ''"
            readonly
          />
          <Button
            :icon="copySuccess === 'audio' ? 'pi pi-check' : 'pi pi-copy'"
            :severity="copySuccess === 'audio' ? 'success' : 'secondary'"
            outlined
            class="ml-2 flex-shrink-0"
            @click="copyToClipboard(ad.s3_signed_url, 'audio')"
            v-tooltip.top="'Copy Audio URL'"
          />
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-content-between w-full">
        <Button
          label="Copy All Details"
          icon="pi pi-copy"
          :severity="copySuccess === 'all' ? 'success' : 'primary'"
          @click="copyAllDetails"
        />
        <Button
          label="Close"
          severity="secondary"
          text
          @click="$emit('update:visible', false)"
        />
      </div>
    </template>
  </Dialog>
</template>

<script>
import { DateTime } from 'luxon';

export default {
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    ad: {
      type: Object,
      default: null,
    },
    apiBaseUrl: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      copySuccess: null,
      copyTimeout: null,
    };
  },
  computed: {
    vastTagUrl() {
      if (!this.ad || !this.ad.generated_ad_id) return '';

      const baseUrl = this.apiBaseUrl || process.env.VUE_APP_API_SERVER || '';
      const cleanBase = baseUrl.replace(/\/+$/, '');
      return `${cleanBase}/ssp/serve_vast_ad.php?id=${this.ad.generated_ad_id}`;
    },
    ekozStartPixelUrl() {
      if (!this.ad || !this.ad.generated_ad_id) return '';

      const baseUrl = this.apiBaseUrl || process.env.VUE_APP_API_SERVER || '';
      const cleanBase = baseUrl.replace(/\/+$/, '');
      return `${cleanBase}/ssp/log_impression.php?id=${this.ad.generated_ad_id}&event=start`;
    },
    trackingPixels() {
      if (!this.ad || !this.ad.ext_track_pixel) return [];
      return this.ad.ext_track_pixel
        .split(/\r?\n/)
        .map((pixel) => pixel.trim())
        .filter(Boolean);
    },
  },
  methods: {
    formatDate(dateStr) {
      if (!dateStr) return '-';
      return DateTime.fromISO(dateStr).toLocaleString(DateTime.DATE_MED);
    },
    async copyToClipboard(text, key) {
      if (!text) return;

      try {
        await navigator.clipboard.writeText(text);
        this.showCopySuccess(key);
      } catch {
        this.fallbackCopy(text, key);
      }
    },
    fallbackCopy(text, key) {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      this.showCopySuccess(key);
    },
    showCopySuccess(key) {
      this.copySuccess = key;
      if (this.copyTimeout) clearTimeout(this.copyTimeout);
      this.copyTimeout = setTimeout(() => {
        this.copySuccess = null;
      }, 2000);

      this.$toast.add({
        severity: 'success',
        detail: 'Copied to clipboard',
        life: 2000,
      });
    },
    async copyAllDetails() {
      if (!this.ad) return;

      const details = [
        '=== Campaign Setup Details ===',
        '',
        `Campaign Name: ${this.ad.campaign_name || '-'}`,
        `Start Date: ${this.formatDate(this.ad.start_date)}`,
        `End Date: ${this.formatDate(this.ad.end_date)}`,
        `CPM Rate: $${Number(this.ad.bid_cap || 0).toFixed(2)}`,
        `Impression Goal: ${Number(this.ad.imp_goal || 0).toLocaleString()}`,
        `Frequency Cap: ${this.ad.frequency_cap || 'None'}`,
        `Ad Run Slot: ${this.ad.ad_run_slot || '-'}`,
        `Ad Length: ${this.ad.ad_length ? `${this.ad.ad_length}s` : '-'}`,
        '',
        `VAST Tag URL: ${this.vastTagUrl}`,
        '',
        `Ekoz Start Pixel: ${this.ekozStartPixelUrl}`,
        '',
        'Tracking Pixels:',
        ...(this.trackingPixels.length ? this.trackingPixels : ['None']),
        '',
        `Audio Ad URL: ${this.ad.s3_signed_url || '-'}`,
      ].join('\n');

      await this.copyToClipboard(details, 'all');
    },
  },
  beforeUnmount() {
    if (this.copyTimeout) clearTimeout(this.copyTimeout);
  },
};
</script>

<style lang="scss" scoped>
.detail-section {
  margin-bottom: 0.5rem;
}

.detail-row {
  display: flex;
  align-items: baseline;
  padding: 0.4rem 0;

  .detail-label {
    font-size: 0.875rem;
    color: var(--gray-500);
    min-width: 140px;
    flex-shrink: 0;
  }

  .detail-value {
    font-weight: 600;
    word-break: break-all;
  }
}

.vast-tag-container {
  display: flex;
  align-items: center;
}
</style>
